import SVGIcon from 'components/SVGIcon/SVGIcon';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { AiMatchedProduct, Language, Product, UomElement } from 'types/schema';
import utilsMessages from 'utils/messages';
import { removeParentheses } from 'utils/strings';
import weightIcon from '../images/weightIcon.svg';
import ChargeBasedOnActualWeightIconModal from 'components/ChargeBasedOnActualWeightIconModal';
export const SPECIAL_UOM = ['Kg', 'L/Lít/Liter'];
import styled from 'styled-components';
import translations from 'translations';

export const UomKg = styled.div`
  background: #f7f7f8;
  border-radius: 4px;
  padding: 1px 6px 2px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 16px;
  width: fit-content;
  cursor: pointer;
  font-weight: 500;
  @media (max-width: 767px) {
    font-size: 10px;
    line-height: 14px;
    svg {
      width: 10px !important;
    }
  }
  ${({ textOnly }) =>
    textOnly &&
    `
    padding: 0 !important;
    background: none;
    color: rgba(255, 255, 255, 0.6);
  `};
`;

const useProductUomInfo = (product: Product | AiMatchedProduct, prefix?: React.ReactNode) => {
  const { uom, netUom, baseUom, baseUnit, netUnit } = product;
  const lang = localStorage.getItem('lang') || Language.Vi;
  const baseUoms = JSON.parse(localStorage.getItem('baseUoms') || '{}');
  const netUoms = JSON.parse(localStorage.getItem('netUoms') || '{}');
  const uoms = JSON.parse(localStorage.getItem('uoms') || '{}');
  const langName = lang === Language.Vi ? 'nameVn' : 'nameEn';

  const SPECIAL_UOM = ['Kg', 'L/Lít/Liter'];

  const currUom: string = removeParentheses(uom ? uoms?.[uom]?.[langName] : '');
  const currNetUom: string = netUom ? netUoms?.[netUom]?.[langName] : '';
  const currBaseUom: string = useMemo(() => {
    if (!baseUom || !baseUoms) return '';
    const arrBaseUoms = Object.values(baseUoms);
    const result = arrBaseUoms.find((item: UomElement) => item[langName] === baseUom);
    return result?.[langName] || '';
  }, [baseUom, baseUoms, langName]);

  const isSpecialUom = useMemo(() => {
    if (currNetUom === SPECIAL_UOM[0] && currUom === SPECIAL_UOM[0]) return true;

    if (SPECIAL_UOM.length > 1 && SPECIAL_UOM[1].includes('L') && currNetUom?.includes('L')) {
      if (lang === Language.Vi && currUom?.includes('Lít')) return true;
      if (lang === Language.En && currUom?.includes('Liter')) return true;
    }

    return false;
  }, [currNetUom, currUom, lang, SPECIAL_UOM]);

  const uomInfo = useMemo(() => {
    if (!currUom) return '';

    const isSameBaseUom = currBaseUom.toLowerCase() === currUom.toLowerCase();
    // const isSameNetUom = currNetUom === currUom;

    if (baseUnit && currBaseUom && netUnit && currNetUom) {
      if (isSameBaseUom && baseUnit === 1) {
        return isSpecialUom ? renderUomLabel(currUom, prefix) : `${netUnit}${currNetUom.toLowerCase()}/${currUom}`;
      }
      return `${netUnit}${currNetUom.toLowerCase()}${' x '}${baseUnit} ${currBaseUom}${isSameBaseUom ? '' : `/${currUom}`}`;
    }
    if (baseUnit && currBaseUom) {
      return `${baseUnit} ${currBaseUom}${!isSameBaseUom ? `/${currUom}` : ''}`;
    }
    if (netUnit && currNetUom) {
      if (isSpecialUom && netUnit === 1) return renderUomLabel(currUom, prefix);
      return `${netUnit}${currNetUom.toLowerCase()}${!isSpecialUom ? `/${currUom}` : ''}`;
    }
    return renderUomLabel(currUom, prefix);
  }, [baseUnit, currBaseUom, netUnit, currNetUom, currUom]);

  return {
    uomInfo: uomInfo,
    uom: currUom,
    baseUom: currBaseUom,
    netUom: currNetUom,
  };
};

function renderUomLabel(uom: string, prefix: React.ReactNode = `${translations(utilsMessages.dvt)}:`) {
  return (
    <>
      {uom === 'Kg' ? (
        <ChargeBasedOnActualWeightIconModal>
          <UomKg>
            <SVGIcon src={weightIcon} />
            <FormattedMessage {...utilsMessages.soldBy} /> {uom}
          </UomKg>
        </ChargeBasedOnActualWeightIconModal>
      ) : (
        <>
          {prefix} {uom}
        </>
      )}
    </>
  );
}

export default useProductUomInfo;
